/**
 * LinkGraphPage — D3 force-directed graph of a project's internal link structure.
 * Route: /seo/link-graph (seo module gate).
 *
 * Preserves the 30px content-area padding per spec (CLAUDE.md → Known Gotchas).
 * Does NOT use ContentLayout or negative margins.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as d3Force from 'd3-force';
import * as d3Selection from 'd3-selection';
import { Network, RefreshCw, Loader2, Info } from 'lucide-react';
import { GlassCard, GlassButton, ModuleHeader } from '../ui';
import { getLinkGraph, reindexProjectLinks } from '../../services/api';
import { apiClient } from '../../services/api';
import './LinkGraphPage.css';

interface GraphNode {
    task_id: string;
    title: string;
    url: string;
    stream: string | null;
    inbound: number;
    outbound: number;
    orphan: boolean;
    // D3 simulation properties
    x?: number;
    y?: number;
    vx?: number;
    vy?: number;
    fx?: number | null;
    fy?: number | null;
}

interface GraphEdge {
    from: string;
    to: string;
    anchor_text: string;
    mode: 'manual' | 'suggested_accepted' | 'auto';
    // D3 simulation properties
    source?: GraphNode | string;
    target?: GraphNode | string;
}

const EDGE_OPACITY: Record<string, number> = {
    manual: 1.0,
    suggested_accepted: 0.7,
    auto: 0.4,
};

const LinkGraphPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const svgRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const [projects, setProjects] = useState<any[]>([]);
    const [projectId, setProjectId] = useState<string>(searchParams.get('project') || '');
    const [graphData, setGraphData] = useState<{ nodes: GraphNode[]; edges: GraphEdge[] } | null>(null);
    const [loading, setLoading] = useState(false);
    const [reindexing, setReindexing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load project list
    useEffect(() => {
        apiClient.get('/projects').then(r => {
            const list = r.data?.projects || r.data || [];
            setProjects(list);
            if (!projectId && list.length > 0) {
                setProjectId(list[0].project_id);
            }
        }).catch(() => {});
    }, []);

    // Sync projectId to URL
    useEffect(() => {
        if (projectId) {
            setSearchParams({ project: projectId }, { replace: true });
        }
    }, [projectId]);

    const fetchGraph = useCallback(async (pid: string) => {
        if (!pid) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getLinkGraph(pid);
            setGraphData(data as any);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to load link graph');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (projectId) fetchGraph(projectId);
    }, [projectId, fetchGraph]);

    const handleReindex = async () => {
        if (!projectId) return;
        setReindexing(true);
        try {
            await reindexProjectLinks(projectId);
            await fetchGraph(projectId);
        } catch {
            // silent
        } finally {
            setReindexing(false);
        }
    };

    // D3 rendering
    useEffect(() => {
        if (!graphData || !svgRef.current) return;

        const svg = d3Selection.select(svgRef.current);
        svg.selectAll('*').remove();

        const width = svgRef.current.clientWidth || 800;
        const height = svgRef.current.clientHeight || 500;

        // Build D3-compatible nodes and links
        const nodes: GraphNode[] = graphData.nodes.map(n => ({ ...n }));
        const nodeMap = new Map(nodes.map(n => [n.task_id, n]));
        const links = graphData.edges.map(e => ({
            ...e,
            source: nodeMap.get(e.from) || e.from,
            target: nodeMap.get(e.to) || e.to,
        }));

        const simulation = d3Force.forceSimulation(nodes as any)
            .force('link', d3Force.forceLink(links as any).id((d: any) => d.task_id).distance(80))
            .force('charge', d3Force.forceManyBody().strength(-150))
            .force('center', d3Force.forceCenter(width / 2, height / 2))
            .force('collision', d3Force.forceCollide(18));

        const g = svg.append('g');

        // Zoom support
        const zoom = (d3Selection as any).zoom?.();
        if (zoom) {
            svg.call(zoom.on('zoom', (event: any) => {
                g.attr('transform', event.transform);
            }));
        }

        // Edges
        const link = g.append('g')
            .selectAll('line')
            .data(links)
            .enter()
            .append('line')
            .attr('class', 'lg-edge')
            .attr('stroke', 'var(--stroke-strong, rgba(15,23,42,0.25))')
            .attr('stroke-opacity', (d: any) => EDGE_OPACITY[d.mode] || 0.6)
            .attr('stroke-width', 1.5)
            .attr('marker-end', 'url(#arrowhead)');

        // Arrow marker
        svg.append('defs').append('marker')
            .attr('id', 'arrowhead')
            .attr('viewBox', '0 -5 10 10')
            .attr('refX', 18)
            .attr('refY', 0)
            .attr('markerWidth', 6)
            .attr('markerHeight', 6)
            .attr('orient', 'auto')
            .append('path')
            .attr('d', 'M0,-5L10,0L0,5')
            .attr('fill', 'var(--stroke-strong, rgba(15,23,42,0.4))');

        // Nodes
        const nodeGroup = g.append('g')
            .selectAll('g')
            .data(nodes)
            .enter()
            .append('g')
            .attr('class', 'lg-node-group')
            .style('cursor', 'pointer')
            .on('click', (_event: any, d: GraphNode) => {
                window.open(`/preview/${d.task_id}`, '_blank');
            })
            .on('mouseover', (event: MouseEvent, d: GraphNode) => {
                if (tooltipRef.current) {
                    tooltipRef.current.style.display = 'block';
                    tooltipRef.current.style.left = (event.offsetX + 12) + 'px';
                    tooltipRef.current.style.top = (event.offsetY - 10) + 'px';
                    tooltipRef.current.innerHTML =
                        `<strong>${d.title}</strong><br/>` +
                        `Inbound: ${d.inbound} &nbsp; Outbound: ${d.outbound}` +
                        (d.stream ? `<br/>Stream: ${d.stream}` : '') +
                        (d.orphan ? '<br/><em style="color:var(--danger)">Orphan — no inbound links</em>' : '');
                }
            })
            .on('mouseout', () => {
                if (tooltipRef.current) tooltipRef.current.style.display = 'none';
            });

        // Drag behaviour
        const drag = (d3Force as any).drag?.() || (d3Selection as any).drag?.();
        if (drag) {
            nodeGroup.call(
                drag
                    .on('start', (event: any, d: any) => {
                        if (!event.active) simulation.alphaTarget(0.3).restart();
                        d.fx = d.x; d.fy = d.y;
                    })
                    .on('drag', (event: any, d: any) => { d.fx = event.x; d.fy = event.y; })
                    .on('end', (event: any, d: any) => {
                        if (!event.active) simulation.alphaTarget(0);
                        d.fx = null; d.fy = null;
                    })
            );
        }

        nodeGroup.append('circle')
            .attr('r', 8)
            .attr('fill', (d: GraphNode) => d.orphan ? 'var(--danger, #ef4444)' : 'var(--primary, #2177FF)')
            .attr('stroke', 'var(--panel, rgba(255,255,255,0.8))')
            .attr('stroke-width', 2);

        // Node labels
        nodeGroup.append('text')
            .attr('class', 'lg-node-label')
            .attr('dy', '0.31em')
            .attr('x', 12)
            .text((d: GraphNode) =>
                d.title.length > 20 ? d.title.slice(0, 20) + '…' : d.title
            )
            .style('font-size', '10px')
            .style('fill', 'var(--text)')
            .style('pointer-events', 'none');

        simulation.on('tick', () => {
            link
                .attr('x1', (d: any) => (d.source as GraphNode).x || 0)
                .attr('y1', (d: any) => (d.source as GraphNode).y || 0)
                .attr('x2', (d: any) => (d.target as GraphNode).x || 0)
                .attr('y2', (d: any) => (d.target as GraphNode).y || 0);

            nodeGroup.attr('transform', (d: GraphNode) => `translate(${d.x || 0},${d.y || 0})`);
        });

        return () => { simulation.stop(); };
    }, [graphData]);

    const nodeCount = graphData?.nodes?.length ?? 0;
    const edgeCount = graphData?.edges?.length ?? 0;
    const orphanCount = graphData?.nodes?.filter(n => n.orphan).length ?? 0;

    return (
        <div className="link-graph-page">
            <ModuleHeader
                title="Link Graph"
                breadcrumb={['SEO', 'Link Graph']}
            />

            <GlassCard variant="default" padding="md" className="lg-controls">
                <div className="lg-controls-row">
                    <div className="lg-project-picker">
                        <label className="lg-label">Project</label>
                        <select
                            className="lg-select"
                            value={projectId}
                            onChange={e => setProjectId(e.target.value)}
                        >
                            {projects.map(p => (
                                <option key={p.project_id} value={p.project_id}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="lg-legend">
                        <span className="lg-legend-item">
                            <span className="lg-legend-dot lg-dot-connected" />
                            Connected
                        </span>
                        <span className="lg-legend-item">
                            <span className="lg-legend-dot lg-dot-orphan" />
                            Orphan (no inbound)
                        </span>
                        <span className="lg-legend-item lg-edge-legend">
                            <span className="lg-edge-line lg-edge-manual" />Manual
                        </span>
                        <span className="lg-legend-item lg-edge-legend">
                            <span className="lg-edge-line lg-edge-suggested" />AI-suggested
                        </span>
                        <span className="lg-legend-item lg-edge-legend">
                            <span className="lg-edge-line lg-edge-auto" />Auto
                        </span>
                    </div>

                    <div className="lg-actions">
                        <GlassButton
                            size="sm"
                            variant="secondary"
                            icon={reindexing ? <Loader2 size={14} className="spinning" /> : <RefreshCw size={14} />}
                            disabled={reindexing || !projectId}
                            onClick={handleReindex}
                            title="Re-index all published articles (costs 1 embedding call per article)"
                        >
                            {reindexing ? 'Indexing…' : 'Re-index Project'}
                        </GlassButton>
                    </div>
                </div>

                {!loading && graphData && (
                    <div className="lg-stats">
                        <span>{nodeCount} articles</span>
                        <span>{edgeCount} links</span>
                        {orphanCount > 0 && (
                            <span className="lg-orphan-stat">
                                <Info size={12} />
                                {orphanCount} orphan{orphanCount !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                )}
            </GlassCard>

            <GlassCard variant="default" padding="none" className="lg-graph-card">
                {loading && (
                    <div className="lg-placeholder">
                        <Loader2 size={24} className="spinning" />
                        <span>Loading graph…</span>
                    </div>
                )}
                {error && !loading && (
                    <div className="lg-placeholder lg-error">
                        <Info size={20} />
                        <span>{error}</span>
                    </div>
                )}
                {!loading && !error && graphData && nodeCount === 0 && (
                    <div className="lg-placeholder">
                        <Network size={24} />
                        <span>No indexed articles yet. Click "Re-index Project" to start.</span>
                    </div>
                )}
                {!loading && !error && graphData && nodeCount > 0 && (
                    <div className="lg-svg-container">
                        <svg ref={svgRef} className="lg-svg" />
                        <div ref={tooltipRef} className="lg-tooltip" style={{ display: 'none' }} />
                    </div>
                )}
            </GlassCard>
        </div>
    );
};

export default LinkGraphPage;
